import { enableSupplierProductLists, isBillingEnabledFor } from '@/config';
import {
  BillingDetailsEditPath,
  BillingHomePath,
  BillingPaymentMethodAddPath,
  BillingPaymentMethodListPath,
} from '@/modules/billing/paths';
import {
  ProductAddPath,
  ProductEditPath,
  ProductImportPath,
  ProductListAddPath,
  ProductListEditPath,
  ProductListListPath,
  ProductListPath,
  ProductListPreviewPath,
  ProductListProductListPath,
  ProductListRetailerListPath,
  ProductListStoreListPath,
  PromotionAddPath,
  PromotionEditPath,
  PromotionListPath,
} from '@/modules/product/paths';
import { Section } from '@/modules/settings/pages/Settings';
import { CustomerListPath, SupplierSettingsPath } from '@/modules/supplier/paths';
import {
  BellIcon,
  BuildingOfficeIcon,
  CreditCardIcon,
  CubeIcon,
  LockClosedIcon,
  PercentBadgeIcon,
  RectangleStackIcon,
  UserIcon,
  UsersIcon,
} from '@heroicons/react/24/outline';

const isSection = (value: Section | undefined): value is Section => Boolean(value);

export const getSupplierSettingsSections = (
  paymentStatus?: boolean,
  productListsEnabled = enableSupplierProductLists,
): Section[] => {
  const productSections: Array<Section | undefined> = [
    {
      href: ProductListPath,
      title: 'Products',
      icon: CubeIcon,
      children: [
        { href: ProductAddPath, title: 'Add Product', icon: CubeIcon },
        { href: ProductEditPath.path, title: 'Edit Product', icon: CubeIcon },
        { href: ProductImportPath, title: 'Import Products', icon: CubeIcon },
      ],
    },
    productListsEnabled
      ? {
          href: ProductListListPath,
          title: 'Product Lists',
          icon: RectangleStackIcon,
          children: [
            {
              href: ProductListAddPath,
              title: 'Create Product List',
              icon: RectangleStackIcon,
            },
            {
              href: ProductListEditPath.path,
              title: 'Edit Product List',
              icon: RectangleStackIcon,
            },
            {
              href: ProductListProductListPath.path,
              title: 'Products in List',
              icon: RectangleStackIcon,
            },
            {
              href: ProductListRetailerListPath.path,
              title: 'Retailer Assignments',
              icon: RectangleStackIcon,
            },
            {
              href: ProductListStoreListPath.path,
              title: 'Store Assignments',
              icon: RectangleStackIcon,
            },
            {
              href: ProductListPreviewPath.path,
              title: 'Effective Portfolio Preview',
              icon: RectangleStackIcon,
            },
          ],
        }
      : undefined,
    {
      href: PromotionListPath,
      title: 'Promotions',
      icon: PercentBadgeIcon,
      children: [
        { href: PromotionAddPath, title: 'Add Promotion', icon: PercentBadgeIcon },
        {
          href: PromotionEditPath.path,
          title: 'Edit Promotion',
          icon: PercentBadgeIcon,
        },
      ],
    },
  ];

  const sections: Array<Section | undefined> = [
    {
      href: SupplierSettingsPath.profile,
      title: 'Profile',
      icon: UserIcon,
      children: [
        { href: SupplierSettingsPath.profile, title: 'Profile Details', icon: UserIcon },
        { href: SupplierSettingsPath.notification, title: 'Notifications', icon: BellIcon },
        // {href: SupplierSettingsPath.currency, title: 'Currency', icon: CurrencyDollarIcon},
        // {href: SupplierSettingsPath.language, title: 'Language', icon: LanguageIcon},
        { href: SupplierSettingsPath.password, title: 'Change Password', icon: LockClosedIcon },
        {
          href: SupplierSettingsPath.businessAddress,
          title: 'Business Address',
          icon: BuildingOfficeIcon,
        },
      ],
    },
    paymentStatus && isBillingEnabledFor('supplier')
      ? {
          href: BillingHomePath,
          title: 'Billing',
          icon: CreditCardIcon,
          children: [
            {
              href: BillingHomePath,
              title: 'Billing',
              icon: CreditCardIcon,
              children: [
                {
                  href: BillingDetailsEditPath,
                  title: 'Edit Billing Details',
                  icon: CreditCardIcon,
                },
              ],
            },
            {
              href: BillingPaymentMethodListPath,
              title: 'Payment Methods',
              icon: CreditCardIcon,
              children: [
                {
                  href: BillingPaymentMethodAddPath,
                  title: 'Add Payment Method',
                  icon: CreditCardIcon,
                },
              ],
            },
          ],
        }
      : undefined,
    {
      href: ProductListPath,
      title: 'Products',
      icon: CubeIcon,
      children: productSections.filter(isSection),
    },
    { href: CustomerListPath, title: 'Customers', icon: UsersIcon },
  ];

  return sections.filter(isSection);
};
